import { useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { MaintCardShell } from './MaintCardShell';
import { ConfirmApplyDialog } from './ConfirmApplyDialog';

export default function JobTypeNumbersCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [applying, setApplying] = useState(false);

  const scan = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/maintenance/job-type-numbers/scan');
      setData(res.data);
      const all = {};
      (res.data.proposed || []).forEach((_, idx) => { all[idx] = true; });
      setSelected(all);
    } catch (e) { toast.error(e.response?.data?.detail || 'Scan failed'); }
    finally { setLoading(false); }
  };

  const toggle = (idx, checked) => setSelected((s) => ({ ...s, [idx]: checked }));
  const selectedCount = Object.values(selected).filter(Boolean).length;

  const buildMapping = () => {
    const mapping = {};
    (data?.proposed || []).forEach((p, idx) => { if (selected[idx]) mapping[p.job_id] = p.proposed_number; });
    return mapping;
  };

  const apply = async () => {
    setApplying(true);
    try {
      const mapping = buildMapping();
      const res = await api.post('/admin/maintenance/job-type-numbers/apply', { mapping });
      toast.success(`${res.data.total_updated} job(s) renumbered`);
      setConfirmOpen(false);
      scan();
    } catch (e) { toast.error(e.response?.data?.detail || 'Apply failed'); }
    finally { setApplying(false); }
  };

  return (
    <MaintCardShell
      title="Job numbers by type (WC/PI/FS/SI/OW)"
      description="Renumbers existing jobs with a 2-letter prefix per work type — Well Construction=WC, Pump Installer=PI, Filters & Service=FS, Site Inspection=SI, Office Work=OW — instead of the generic JOB- prefix. Never touches public_token."
      testId="job-type-numbers"
      count={data ? data.total_to_renumber : null}
      loading={loading}
      onScan={scan}
    >
      {data && data.proposed.length > 0 && (
        <div className="space-y-3">
          <div className="space-y-1 max-h-80 overflow-y-auto">
            {data.proposed.map((p, idx) => (
              <label key={p.job_id} className="flex items-center gap-2 text-xs border border-gray-100 rounded-lg p-2 cursor-pointer" data-testid={`job-type-numbers-row-${idx}`}>
                <Checkbox data-testid={`job-type-numbers-row-${idx}-checkbox`} checked={!!selected[idx]} onCheckedChange={(v) => toggle(idx, !!v)} />
                <span className="text-gray-500">{p.current_number || '(sin número)'}</span>
                <span className="text-gray-400">→</span>
                <span className="font-semibold text-emerald-700">{p.proposed_number}</span>
              </label>
            ))}
          </div>
          <Button data-testid="job-type-numbers-apply-btn" size="sm" disabled={selectedCount === 0} onClick={() => setConfirmOpen(true)} className="bg-red-600 hover:bg-red-700 text-white">
            Apply selected ({selectedCount})
          </Button>
        </div>
      )}
      {data && data.proposed.length === 0 && <p className="text-xs text-emerald-600">All jobs already use the per-type numbering.</p>}

      <ConfirmApplyDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={apply}
        confirming={applying}
        title="Renumber jobs by type"
        summary={`This will rename job_number on ${selectedCount} job(s). Only job_number + activity log are touched — never public_token or any other field.`}
      />
    </MaintCardShell>
  );
}
